import { useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { AlertTriangle, Save } from 'lucide-react'
import { defekte as defekteApi, kunden as kundenApi, geraete as geraeteApi } from '../../lib/api'
import { TopBar } from '../../components/layout/TopBar'
import { Input } from '../../components/ui/Input'
import { Select } from '../../components/ui/Select'
import { Textarea } from '../../components/ui/Textarea'
import { Button } from '../../components/ui/Button'
import { Card } from '../../components/ui/Card'
import type { Defekt } from '../../types'

const schema = z.object({
  kunde_id: z.string().min(1, 'Kunde erforderlich'),
  geraet_id: z.string().optional(),
  beschreibung: z.string().min(5, 'Beschreibung erforderlich'),
  zustand: z.enum(['leicht', 'mittel', 'schwer', 'total-ausfall']),
  ursache: z.string().optional(),
  massnahmen: z.string().optional(),
  status: z.enum(['offen', 'in-bearbeitung', 'behoben']),
})

type FormData = z.infer<typeof schema>

export function DefektFormPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const qc = useQueryClient()

  const { data: kundenList } = useQuery({ queryKey: ['kunden'], queryFn: () => kundenApi.list() })
  const kundeId = searchParams.get('kunde_id')
  const { data: geraeteList } = useQuery({
    queryKey: ['geraete', kundeId],
    queryFn: () => geraeteApi.list(kundeId ?? undefined),
    enabled: !!kundeId,
  })

  const { register, handleSubmit, formState: { errors }, watch } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      kunde_id: kundeId ?? '',
      geraet_id: searchParams.get('geraet_id') ?? '',
      besuch_id: searchParams.get('besuch_id') ?? '',
      zustand: 'mittel',
      status: 'offen',
    } as FormData & { besuch_id?: string },
  })

  const selectedKundeId = watch('kunde_id')

  const mutation = useMutation({
    mutationFn: (data: Partial<Defekt>) => defekteApi.create({
      ...data,
      besuch_id: searchParams.get('besuch_id') ?? undefined,
    }),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ['defekte'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      navigate(`/defekte/${d.id}`)
    },
  })

  return (
    <>
      <TopBar title="Defekt melden" backTo="/defekte" />
      <div className="px-4 py-3 pb-8">
        <form onSubmit={handleSubmit((d) => mutation.mutate(d as unknown as Partial<Defekt>))} className="space-y-4">
          <Card className="bg-red-50 border-red-200">
            <div className="flex items-center gap-3 mb-1">
              <AlertTriangle size={20} className="text-red-500" />
              <h3 className="font-semibold text-red-900">Defekt erfassen</h3>
            </div>
            <p className="text-sm text-red-700">Beschreibe den Defekt so genau wie möglich.</p>
          </Card>

          <Card>
            <div className="space-y-4">
              <Select
                label="Kunde" required
                options={(kundenList ?? []).map((k) => ({ value: k.id, label: k.firma ?? k.name }))}
                placeholder="Kunde auswählen"
                error={errors.kunde_id?.message}
                {...register('kunde_id')}
              />
              {selectedKundeId && geraeteList && geraeteList.length > 0 && (
                <Select
                  label="Gerät"
                  options={geraeteList.map((g) => ({ value: g.id, label: `${g.modell} (${g.seriennummer ?? 'keine SN'})` }))}
                  placeholder="Gerät auswählen (optional)"
                  {...register('geraet_id')}
                />
              )}
              <Textarea
                label="Beschreibung des Defekts" required
                placeholder="Was ist genau defekt? Was passiert? Fehlermeldung?"
                rows={4}
                error={errors.beschreibung?.message}
                {...register('beschreibung')}
              />
              <Select
                label="Zustand / Schweregrad" required
                options={[
                  { value: 'leicht', label: 'Leicht – Gerät funktioniert noch' },
                  { value: 'mittel', label: 'Mittel – Eingeschränkte Funktion' },
                  { value: 'schwer', label: 'Schwer – Kaum funktionsfähig' },
                  { value: 'total-ausfall', label: 'Totalausfall – Gerät ausgefallen' },
                ]}
                {...register('zustand')}
              />
              <Select
                label="Ursache"
                options={[
                  { value: '', label: '– Ursache auswählen –' },
                  { value: 'Benutzerfehler', label: 'Benutzerfehler / falsche Bedienung' },
                  { value: 'Verschleiss', label: 'Normaler Verschleiss' },
                  { value: 'Fabrikfehler', label: 'Fabrikfehler / Herstellungsmangel' },
                  { value: 'Unfall', label: 'Unfall / mechanische Beschädigung' },
                  { value: 'Kalk', label: 'Kalkablagerungen / Wartungsmangel' },
                  { value: 'Elektrisch', label: 'Elektrischer Defekt' },
                  { value: 'Unbekannt', label: 'Unbekannt' },
                ]}
                {...register('ursache')}
              />
              <Textarea
                label="Massnahmen / Was wurde gemacht"
                placeholder="Bereits ergriffene Massnahmen..."
                rows={3}
                {...register('massnahmen')}
              />
              <Select
                label="Status"
                options={[
                  { value: 'offen', label: 'Offen' },
                  { value: 'in-bearbeitung', label: 'In Bearbeitung' },
                  { value: 'behoben', label: 'Behoben' },
                ]}
                {...register('status')}
              />
            </div>
          </Card>

          {mutation.error && <p className="text-sm text-red-600 text-center">{mutation.error.message}</p>}
          <Button type="submit" fullWidth size="lg" icon={<Save size={18} />} loading={mutation.isPending} variant="danger">
            Defekt speichern
          </Button>
        </form>
      </div>
    </>
  )
}
